import { useEffect, useId, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NexaGlyph } from '@/components/nexa-mark/nexa-glyph';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { IOperatorAlertV1 } from '@/lib/alerts/alert.types';
import { alertView, groupView, type IAlertGroupView, type IAlertView } from '@/lib/alerts/alert.view';
import {
  acknowledge,
  catchUp,
  dismissDrop,
  openSummary,
  useAlertsStore,
} from '@/lib/alerts/alerts.store';
import { alertLine, kindLine, pairedLine } from './alert-line';

// The alert cards, in the «Operar» design line (Nexa's navy card of
// `routes/disruptions/operar/trabajo/work-switch.tsx`). ONE card, three uses:
//   · the EVIDENT dialog (an operator present on another screen): the strip (what · where ·
//     when, like WorkSwitch's — monospace for CODES only: the flight number, the IATA
//     airports of a disruption, the clock; prose never), what happened, the way to it
//     («Operar» / «Ver vuelo»), a burst grouped into it («y 4 más» → «Ver todas»). The
//     burst's lead is `byLead`'s (a cancellation before the disruption it opened); that
//     disruption reads as the lead's second line, not «y 1 más», and «Operar» goes to the
//     burst's disruption even when a flight leads (`groupView`);
//   · the DROP (an operator working a disruption in «Operar», PO 2026-10-29): the same
//     card, dropped from under the Operar header, gone by itself 10 s after its last news
//     — held while the pointer or the focus is on it; never modal, never a focus grab (the
//     polite live region speaks it). Gone by itself it acknowledges nothing: the bell
//     keeps it unread;
//   · the SUMMARY list (on return from away, or asked for from the bell / the dialog's
//     «Ver todas»), with its four states — loading, error (+ retry), empty, the list; a
//     row says a disruption by its reason, an operator's own title under it.
//
// Brief §6 inv. 8: NOT portalled. The layer is `absolute inset-0` inside the frame that
// renders it — the shell's content area (app-shell.tsx) or, on Operar, the stage under
// the header (operar-route.tsx), like WorkSwitch — so the rehearsal strip (#812) above the
// frame is never covered, and nothing floats (#735). Radix Dialog without its Portal
// gives the focus trap, Esc and the outside click; every way of closing acknowledges what
// the card showed (`acknowledge` → `POST /v1/alerts/read { upTo }`).

/** Nexa's filled / quiet actions on the navy card (nexa-guide.tsx's pair). */
const FILLED = 'h-11 bg-nexa-blue font-bold text-white hover:bg-nexa-blue/90';
const QUIET = 'h-11 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white';
const MIST = 'text-sm text-nexa-mist';
/** The strip's codes (WorkSwitch's flight codes). */
const CODES = 'font-mono text-xl font-bold tracking-tight';
const DOT: Readonly<Record<IOperatorAlertV1['severity'], string>> = {
  critical: 'bg-destructive',
  warning: 'bg-nexa-cyan',
  info: 'bg-nexa-mist',
};
const CARD = 'flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-nexa-navy text-white shadow-2xl';
const STRIP = 'flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-white/10 px-5 py-3';
const TITLE = 'flex min-h-9 items-center gap-2 text-lg font-extrabold leading-tight';
const FOOT = 'flex flex-wrap justify-end gap-2 border-t border-white/10 px-5 py-3';

/** The drop's way out (`nexa-lift`), then it is gone. Its 10 s are its bar's. */
const LEAVE_MS = 300;

/** The strip's codes: a flight — its number, then the route; a disruption — its airports. */
function StripCodes({ v }: { v: IAlertView }) {
  return (
    <>
      {v.what ? <span className={CODES}>{v.what}</span> : null}
      {v.where ? <span className={v.what ? MIST : CODES}>{v.where}</span> : null}
      <span className={cn('ml-auto font-mono tabular-nums', MIST)}>{v.when}</span>
    </>
  );
}

/** The title's words: the severity's dot and what happened. */
function Headline({ lead, v }: { lead: IOperatorAlertV1; v: IAlertView }) {
  const { t } = useTranslation('alerts');
  return (
    <>
      <span aria-hidden className={cn('h-2.5 w-2.5 shrink-0 rounded-full', DOT[lead.severity])} />
      {kindLine(t, v)}
    </>
  );
}

/**
 * The card's foot: the lead's ways in, «Ver todas» when it says less than it holds, and
 * «Entendido» — which acknowledges what the card showed (the summary has only that).
 */
function Foot({ g, shown }: { g: IAlertGroupView | null; shown: readonly IOperatorAlertV1[] }) {
  const { t } = useTranslation('alerts');
  return (
    <div className={FOOT}>
      {g ? <Actions alert={g.lead} operate={g.operate} shown={shown} /> : null}
      {g && g.more > 0 ? (
        <Button variant="outline" className={QUIET} onClick={() => openSummary('list')}>
          {t('seeAll')}
        </Button>
      ) : null}
      <Button
        variant="outline"
        className={QUIET}
        onClick={() => acknowledge(shown)}
        data-testid="alerts-ok"
      >
        {t('ok')}
      </Button>
    </div>
  );
}

/** Under the title: the operator's own words, the paired disruption, «y N más». */
function Story({ v, g }: { v: IAlertView; g: IAlertGroupView }) {
  const { t } = useTranslation('alerts');
  const pairedNote = g.paired ? alertView(g.paired).note : null;
  return (
    <>
      {v.note ? (
        <p className={MIST} data-testid="alert-note">
          {v.note}
        </p>
      ) : null}
      {g.paired ? (
        <p className="text-sm font-semibold" data-testid="alert-paired">
          {pairedLine(t, g.paired)}
        </p>
      ) : null}
      {pairedNote ? <p className={MIST}>{pairedNote}</p> : null}
      {g.more > 0 ? (
        <p className="text-sm font-semibold" data-testid="alert-more">
          {t('more', { count: g.more })}
        </p>
      ) : null}
    </>
  );
}

function Actions({
  alert,
  operate,
  shown,
}: {
  alert: IOperatorAlertV1;
  /** «Operar»'s target when it is not the alert's own (a burst's disruption). */
  operate?: string | null;
  shown: readonly IOperatorAlertV1[];
}) {
  const { t } = useTranslation('alerts');
  const navigate = useNavigate();
  const v = alertView(alert);
  const toOperate = operate === undefined ? v.operate : operate;
  // Leaving for a screen acknowledges first (the card closes), then goes.
  const go = (to: string) => () => {
    acknowledge(shown);
    void navigate(to);
  };
  return (
    <>
      {toOperate ? (
        <Button className={FILLED} onClick={go(toOperate)} data-testid="alert-operate">
          {t('operate')}
        </Button>
      ) : null}
      {v.flight ? (
        <Button
          variant="outline"
          className={QUIET}
          onClick={go(v.flight)}
          data-testid="alert-flight"
        >
          {t('viewFlight')}
        </Button>
      ) : null}
    </>
  );
}

function AlertsCard({ mode }: { mode: 'dialog' | 'return' | 'list' }) {
  const { t } = useTranslation('alerts');
  const group = useAlertsStore((s) => s.dialog?.alerts);
  const entries = useAlertsStore((s) => s.entries);
  const sync = useAlertsStore((s) => s.sync);
  const g = mode === 'dialog' && group ? groupView(group) : null;
  const lead = g?.lead;
  const shown = lead ? group! : entries.filter((e) => e.unread).map((e) => e.alert);
  const v = lead ? alertView(lead) : null;
  return (
    <DialogPrimitive.Root open onOpenChange={(open) => (open ? null : acknowledge(shown))}>
      <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/30 p-4 backdrop-blur-[2px]">
        <DialogPrimitive.Content
          role={lead ? 'alertdialog' : 'dialog'}
          data-testid={lead ? 'alert-dialog' : 'alerts-summary'}
          className={cn(CARD, 'max-h-full focus:outline-none motion-safe:animate-in motion-safe:zoom-in-95')}
        >
          <DialogPrimitive.Description asChild>
            {v ? (
              <div className={STRIP} data-testid="alert-strip">
                <StripCodes v={v} />
              </div>
            ) : (
              <p className="sr-only">{t('count', { count: shown.length })}</p>
            )}
          </DialogPrimitive.Description>
          <div className={cn('flex items-start gap-3 px-5 pt-4', lead && 'pb-4')}>
            <NexaGlyph tone="light" className="h-9 w-9 shrink-0" />
            <div className="min-w-0 flex-1 space-y-1">
              <DialogPrimitive.Title className={TITLE}>
                {lead && v ? <Headline lead={lead} v={v} /> : t(`summary.${mode}`)}
              </DialogPrimitive.Title>
              {v && g ? <Story v={v} g={g} /> : null}
            </div>
          </div>
          {/* the summary's ONE scroll region (frontend-conventions §18.1) */}
          {lead ? null : (
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-3" data-sync={sync}>
              {sync === 'loading' ? (
                <p
                  role="status"
                  className={cn('motion-safe:animate-pulse', MIST)}
                  data-testid="alerts-summary-loading"
                >
                  {t('summary.loading')}
                </p>
              ) : null}
              {sync === 'error' ? (
                <div
                  role="alert"
                  className="flex flex-wrap items-center gap-3"
                  data-testid="alerts-summary-error"
                >
                  <p className="min-w-0 flex-1 text-sm">{t('summary.error')}</p>
                  <Button variant="outline" className={QUIET} onClick={() => void catchUp()}>
                    {t('summary.retry')}
                  </Button>
                </div>
              ) : null}
              {shown.length === 0 && sync === 'idle' ? (
                <p className={MIST} data-testid="alerts-summary-empty">
                  {t('summary.empty')}
                </p>
              ) : null}
              <ul className="divide-y divide-white/10">
                {shown.map((a) => {
                  const note = alertView(a).note;
                  return (
                    <li key={a.id} className="flex flex-wrap items-center gap-2 py-2">
                      <span
                        aria-hidden
                        className={cn('h-2 w-2 shrink-0 rounded-full', DOT[a.severity])}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">{alertLine(t, a)}</p>
                        {note ? <p className="text-xs text-nexa-mist">{note}</p> : null}
                      </div>
                      <Actions alert={a} shown={shown} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <Foot g={g} shown={shown} />
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Root>
  );
}

/** Whichever alert card is up — at most one; the summary wins over the evident dialog. */
export function AlertsLayer() {
  const open = useAlertsStore((s) => s.summary ?? (s.dialog ? 'dialog' : null));
  return open ? <AlertsCard mode={open} /> : null;
}

/**
 * The drop's card. Its clock is the bar under it: a 10 s drain (`nexa-drain`,
 * tailwind.config.ts) that CSS holds while the pointer or the focus is on the card
 * (`group-hover` / `group-focus-within`); when it ends the card slides back up and is gone.
 * Reduced motion keeps the clock (`nexa-hold`, a still bar), not the slide. Fresh news
 * remounts the bar: the 10 s run again. «Entendido» and a way in acknowledge.
 */
function DropCard({ drop }: { drop: { alerts: IOperatorAlertV1[] } }) {
  const titleId = useId();
  // Each news grows the card by one (a repeated id never reaches it): its generation.
  const news = drop.alerts.length;
  // The news it was leaving with: fresh news while it slides out brings it back.
  const [leftAt, setLeftAt] = useState<number | null>(null);
  const leaving = leftAt === news;
  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(dismissDrop, LEAVE_MS);
    return () => window.clearTimeout(id);
  }, [leaving]);

  const g = groupView(drop.alerts);
  if (!g) return null;
  const v = alertView(g.lead);
  const shown = drop.alerts;
  return (
    <section
      aria-labelledby={titleId}
      data-testid="alert-drop"
      data-leaving={leaving || undefined}
      // The ring: Operar's own navy band can sit right under it — the card reads apart.
      className={cn(
        CARD,
        'group pointer-events-auto ring-1 ring-white/40',
        leaving ? 'motion-safe:animate-nexa-lift' : 'motion-safe:animate-nexa-drop',
      )}
    >
      <div className={STRIP} data-testid="alert-strip">
        <StripCodes v={v} />
      </div>
      <div className="flex items-start gap-3 px-5 py-4">
        <NexaGlyph tone="light" className="h-9 w-9 shrink-0" />
        <div className="min-w-0 flex-1 space-y-1">
          <h2 id={titleId} className={TITLE}>
            <Headline lead={g.lead} v={v} />
          </h2>
          <Story v={v} g={g} />
        </div>
      </div>
      <Foot g={g} shown={shown} />
      <div
        key={news}
        aria-hidden
        data-testid="alert-drop-clock"
        className="h-1 origin-left bg-nexa-cyan motion-safe:animate-nexa-drain motion-reduce:animate-nexa-hold group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]"
        onAnimationEnd={() => setLeftAt(news)}
      />
    </section>
  );
}

/**
 * The drop, at the top of the Operar stage (operar-route.tsx — under the header, inside
 * the frame, never portalled: brief §6 inv. 8). The band clips the card, so it comes down
 * from under the header and goes back up there; the band itself lets clicks through.
 */
export function AlertsDrop() {
  const drop = useAlertsStore((s) => s.drop);
  if (!drop) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center overflow-hidden px-4 pb-8 pt-3"
      data-testid="alert-drop-band"
    >
      <DropCard key={drop.at} drop={drop} />
    </div>
  );
}
